// ═══════════════════════════════════════════════════════════════
// VolunteerOS — Events Board
// Browse, filter, create, edit, delete, and sign up for events
// ═══════════════════════════════════════════════════════════════

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  Title, Text, TextInput, Select, MultiSelect, Group, Paper, Badge, SimpleGrid,
  Modal, Button, NumberInput, Stack, ActionIcon, Loader, Center,
  Code,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import {
  IconSearch, IconPlus, IconEdit, IconTrash, IconCalendarEvent,
  IconMapPin, IconUsers, IconRefresh, IconShieldCheck,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useVolunteerStore, parseExtras, mergeExtras } from '../../store/volunteerStore';
import type { VolEvent, Volunteer, VolAssignment } from '../../store/volunteerStore';
import { notifications } from '@mantine/notifications';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';

// ── Constants ─────────────────────────────────────────────────

const EVENT_TYPES = [
  { value: 'distribution', label: 'Distribution' },
  { value: 'mucking', label: 'Mucking' },
  { value: 'medical', label: 'Medical' },
  { value: 'intake', label: 'Intake' },
  { value: 'command', label: 'Command' },
  { value: 'shelter', label: 'Shelter' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'planned', label: 'Planned' },
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
];

const STATUS_COLORS: Record<string, string> = {
  planned: 'blue',
  active: 'green',
  closed: 'gray',
};

const TYPE_COLORS: Record<string, string> = {
  distribution: 'teal',
  mucking: 'orange',
  medical: 'red',
  intake: 'indigo',
  command: 'violet',
  shelter: 'yellow',
  other: 'gray',
};

function generateCheckInCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

// ── Blank form ────────────────────────────────────────────────

interface EventForm {
  name: string;
  incidentId: string;
  type: string;
  status: string;
  location: string;
  address: string;
  startDate: string | Date | null;
  endDate: string | Date | null;
  targetVol: number;
  reqCerts: string;
  checkInCode: string;
}

const BLANK_FORM: EventForm = {
  name: '',
  incidentId: '',
  type: 'other',
  status: 'planned',
  location: '',
  address: '',
  startDate: null,
  endDate: null,
  targetVol: 0,
  reqCerts: '',
  checkInCode: '',
};

// ═══════════════════════════════════════════════════════════════

export default function Events() {
  const { canEdit } = useModulePermission('volunteer');
  const {
    events, assignments, volunteers, loading,
    loadEvents, loadAssignments, loadVolunteers,
    createEvent, updateEvent, deleteEvent, createAssignment,
  } = useVolunteerStore();
  const { incidents, activeIncident, user } = useAuthStore();

  // Filters
  const [search, setSearch] = useState('');
  const [incidentFilter, setIncidentFilter] = useState<string>('active');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  // Create / Edit modal
  const [formOpened, { open: openForm, close: closeForm }] = useDisclosure(false);
  const [editingEvent, setEditingEvent] = useState<VolEvent | null>(null);
  const [form, setForm] = useState<EventForm>(BLANK_FORM);
  const [saving, setSaving] = useState(false);

  // Address autocomplete
  const [addrSuggestions, setAddrSuggestions] = useState<{ display: string; lat: string; lon: string }[]>([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const addrTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchAddress = useCallback((query: string) => {
    if (addrTimer.current) clearTimeout(addrTimer.current);
    if (query.length < 4) { setAddrSuggestions([]); setAddrOpen(false); return; }
    addrTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&countrycodes=us`,
          { headers: { 'User-Agent': 'OpenGov-EMC/1.0' } },
        );
        const data = await res.json();
        const suggestions = data.map((d: { display_name: string; lat: string; lon: string }) => ({
          display: d.display_name,
          lat: d.lat,
          lon: d.lon,
        }));
        setAddrSuggestions(suggestions);
        setAddrOpen(suggestions.length > 0);
      } catch { setAddrSuggestions([]); setAddrOpen(false); }
    }, 400);
  }, []);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<VolEvent | null>(null);
  const [viewEvent, setViewEvent] = useState<VolEvent | null>(null);
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);

  // Initial load
  const didLoad = useRef(false);
  useEffect(() => {
    if (!didLoad.current) {
      didLoad.current = true;
      loadEvents();
      loadAssignments();
      loadVolunteers();
    }
  }, [loadEvents, loadAssignments]);

  // Default incident filter to active incident
  useEffect(() => {
    if (activeIncident) setIncidentFilter(String(activeIncident.id));
  }, [activeIncident]);

  // Build cert options from volunteer data + common defaults
  const certOptions = useMemo(() => {
    const set = new Set(['CPR', 'First Aid', 'CERT', 'AED', 'ICS-100', 'ICS-200', 'ICS-700', 'ICS-800', 'HAM Radio', 'HAZWOPER', 'Chainsaw Safety']);
    volunteers.forEach(v => {
      if (v.certs) v.certs.split(',').forEach(c => { const t = c.trim(); if (t) set.add(t); });
    });
    return Array.from(set).sort();
  }, [volunteers]);

  // ── Helpers ───────────────────────────────────────────────

  function assignedCount(eventId: number): number {
    return assignments.filter(
      a => String(a.eventId) === String(eventId) && a.status !== 'cancelled' && a.status !== 'declined',
    ).length;
  }

  // ── Filters ───────────────────────────────────────────────

  let filtered = events;
  if (incidentFilter !== 'all') filtered = filtered.filter(e => String(e.incidentId) === incidentFilter);
  if (statusFilter !== 'all') filtered = filtered.filter(e => e.status === statusFilter);
  if (typeFilter !== 'all') filtered = filtered.filter(e => e.type === typeFilter);
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(e =>
      e.name.toLowerCase().includes(q) ||
      e.location.toLowerCase().includes(q) ||
      e.address.toLowerCase().includes(q),
    );
  }
  filtered = [...filtered].sort((a, b) => (b.datecreated ?? '').localeCompare(a.datecreated ?? ''));

  // ── Create / Edit ─────────────────────────────────────────

  function openCreate() {
    setEditingEvent(null);
    setForm({ ...BLANK_FORM, incidentId: activeIncident ? String(activeIncident.id) : '' });
    openForm();
  }

  function openEdit(ev: VolEvent) {
    setEditingEvent(ev);
    setForm({
      name: ev.name,
      incidentId: ev.incidentId,
      type: ev.type,
      status: ev.status,
      location: ev.location,
      address: ev.address,
      startDate: ev.startDate ? new Date(ev.startDate) : null,
      endDate: ev.endDate ? new Date(ev.endDate) : null,
      targetVol: ev.targetVol,
      reqCerts: ev.reqCerts,
      checkInCode: ev.checkInCode,
    });
    openForm();
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      // Geocode address to get lat/lng
      let lat = '';
      let lng = '';
      const existingExtras = editingEvent ? parseExtras(editingEvent.notes) : {};
      if (form.address.trim()) {
        // Check if we already have a selected suggestion with coordinates
        const match = addrSuggestions.find(s => s.display === form.address);
        if (match) {
          lat = match.lat;
          lng = match.lon;
        } else {
          // Try geocoding
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(form.address.trim())}&limit=1&countrycodes=us`,
              { headers: { 'User-Agent': 'OpenGov-EMC/1.0' } },
            );
            const data = await res.json();
            if (data.length > 0) {
              lat = data[0].lat;
              lng = data[0].lon;
            }
          } catch { /* geocoding failed, continue without coords */ }
        }
      }

      const notesExtras = {
        ...existingExtras,
        ...(lat && lng ? { lat, lng } : {}),
      };

      const payload = {
        name: form.name.trim(),
        incidentId: form.incidentId,
        type: form.type,
        status: form.status,
        location: form.location.trim(),
        address: form.address.trim(),
        startDate: form.startDate ? dayjs(form.startDate).format('YYYY-MM-DD') : '',
        endDate: form.endDate ? dayjs(form.endDate).format('YYYY-MM-DD') : '',
        targetVol: form.targetVol,
        reqCerts: form.reqCerts.trim(),
        checkInCode: form.checkInCode,
        notes: mergeExtras(editingEvent?.notes ?? '', notesExtras),
      };

      if (editingEvent) {
        await updateEvent(editingEvent.id, payload);
      } else {
        await createEvent(payload);
      }
      closeForm();
    } finally {
      setSaving(false);
    }
  }

  // ── Sign Up (self-register) ───────────────────────────────

  async function handleSignUp(ev: VolEvent) {
    const assigned = assignedCount(ev.id);
    if (ev.targetVol > 0 && assigned >= ev.targetVol) {
      notifications.show({ title: 'Full', message: 'This event is at capacity.', color: 'orange' });
      return;
    }
    // Find current user's volunteer record by email
    const myEmail = user?.email?.toLowerCase() || '';
    const myVol = volunteers.find(v => v.email?.toLowerCase() === myEmail);
    const volId = myVol ? String(myVol.id) : '';
    const volName = myVol ? `${myVol.firstName} ${myVol.lastName}`.trim() : user?.fullName || '';

    if (!volId) {
      notifications.show({ title: 'Not Found', message: 'No volunteer profile found for your account. Contact an admin to be added to the roster.', color: 'orange' });
      return;
    }

    // Check if already signed up
    const alreadyAssigned = assignments.some(a =>
      String(a.eventId) === String(ev.id) && String(a.volunteerId) === volId && a.status !== 'cancelled'
    );
    if (alreadyAssigned) {
      notifications.show({ title: 'Already Signed Up', message: 'You are already registered for this event.', color: 'blue' });
      return;
    }

    await createAssignment({
      volunteerId: volId,
      eventId: String(ev.id),
      incidentId: ev.incidentId,
      role: myVol?.rolePref || '',
      status: 'confirmed',
      shiftDate: ev.startDate,
      startTime: '',
      endTime: '',
      checkIn: '',
      checkOut: '',
      hours: '',
      notes: mergeExtras('', { selfRegistered: true, registeredAt: new Date().toISOString(), volunteerName: volName }),
    });
    notifications.show({ title: 'Signed Up', message: `You're registered for ${ev.name}.`, color: 'green' });
  }

  // ── Delete ────────────────────────────────────────────────

  async function handleDelete() {
    if (!deleteTarget) return;
    await deleteEvent(deleteTarget.id);
    closeDelete();
    setDeleteTarget(null);
  }

  // ── Select data ───────────────────────────────────────────

  const incidentOptions = [
    { value: 'all', label: 'All Incidents' },
    ...incidents.filter(i => i.id != null).map(i => ({ value: String(i.id), label: i.name })),
  ];

  const statusFilterOptions = [
    { value: 'all', label: 'All Statuses' },
    ...STATUS_OPTIONS,
  ];

  const typeFilterOptions = [
    { value: 'all', label: 'All Types' },
    ...EVENT_TYPES,
  ];

  const incidentSelectData = incidents
    .filter(i => i.id != null)
    .map(i => ({ value: String(i.id), label: i.name }));

  // ── Render ────────────────────────────────────────────────

  if (loading && events.length === 0) {
    return <Center py="xl"><Loader size="sm" /><Text ml="sm" size="sm" c="dimmed">Loading events...</Text></Center>;
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Events</Title>
        <Group gap="xs">
          <ActionIcon variant="subtle" onClick={() => { loadEvents(); loadAssignments(); }} title="Refresh">
            <IconRefresh size={16} />
          </ActionIcon>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            Create Event
          </Button>}
        </Group>
      </Group>

      {/* Filters */}
      <Group mb="md" gap="sm" wrap="wrap">
        <TextInput
          placeholder="Search events..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.currentTarget.value)}
          size="xs"
          w={220}
        />
        <Select data={incidentOptions} value={incidentFilter} onChange={v => setIncidentFilter(v ?? 'all')} size="xs" w={180} />
        <Select data={statusFilterOptions} value={statusFilter} onChange={v => setStatusFilter(v ?? 'all')} size="xs" w={150} />
        <Select data={typeFilterOptions} value={typeFilter} onChange={v => setTypeFilter(v ?? 'all')} size="xs" w={150} />
      </Group>

      <Text size="sm" c="dimmed" mb="sm">
        {filtered.length} event{filtered.length !== 1 ? 's' : ''}
      </Text>

      {/* Event Cards */}
      {filtered.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No events match your filters.</Text>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {filtered.map(ev => {
            const assigned = assignedCount(ev.id);
            const extras = parseExtras(ev.notes);
            const atCapacity = ev.targetVol > 0 && assigned >= ev.targetVol;
            const inc = incidents.find(i => String(i.id) === String(ev.incidentId));
            return (
              <Paper key={ev.id} p="sm" radius="sm" withBorder
                style={{ cursor: 'pointer' }}
                onClick={() => setViewEvent(ev)}>
                {/* Header badges */}
                <Group justify="space-between" wrap="nowrap" mb={6}>
                  <Group gap="xs">
                    <Badge size="xs" color={TYPE_COLORS[ev.type] ?? 'gray'} tt="capitalize">{ev.type}</Badge>
                    <Badge size="xs" color={STATUS_COLORS[ev.status] ?? 'gray'} variant="outline" tt="capitalize">{ev.status}</Badge>
                  </Group>
                  {canEdit && (
                  <Group gap={4}>
                    <ActionIcon size="xs" variant="subtle" color="blue" onClick={(e) => { e.stopPropagation(); openEdit(ev); }} title="Edit">
                      <IconEdit size={12} />
                    </ActionIcon>
                    <ActionIcon size="xs" variant="subtle" color="red" onClick={(e) => { e.stopPropagation(); setDeleteTarget(ev); openDelete(); }} title="Delete">
                      <IconTrash size={12} />
                    </ActionIcon>
                  </Group>
                  )}
                </Group>

                {/* Name */}
                <Text size="sm" fw={600} mb={4}>{ev.name}</Text>
                {inc && <Text size="xs" c="dimmed" mb={2}>{inc.name}</Text>}

                {/* Location */}
                {ev.location && (
                  <Group gap={4} mb={2}>
                    <IconMapPin size={12} color="var(--mantine-color-dimmed)" />
                    <Text size="xs" c="dimmed">{ev.location}{ev.address ? ` — ${ev.address}` : ''}</Text>
                  </Group>
                )}

                {/* Dates */}
                {ev.startDate && (
                  <Group gap={4} mb={2}>
                    <IconCalendarEvent size={12} color="var(--mantine-color-dimmed)" />
                    <Text size="xs" c="dimmed">
                      {dayjs(ev.startDate).format('MMM D, YYYY')}
                      {ev.endDate && ev.endDate !== ev.startDate ? ` — ${dayjs(ev.endDate).format('MMM D, YYYY')}` : ''}
                    </Text>
                  </Group>
                )}

                {/* Volunteer counts */}
                <Group gap={4} mb={2}>
                  <IconUsers size={12} color="var(--mantine-color-dimmed)" />
                  <Text size="xs" c="dimmed">
                    {assigned}{ev.targetVol > 0 ? ` / ${ev.targetVol}` : ''} volunteer{assigned !== 1 ? 's' : ''}
                    {atCapacity && <Badge size="xs" color="red" ml={4}>Full</Badge>}
                  </Text>
                </Group>

                {/* Required certs */}
                {ev.reqCerts && (
                  <Group gap={4} mb={2}>
                    <IconShieldCheck size={12} color="var(--mantine-color-dimmed)" />
                    <Text size="xs" c="dimmed">Certs: {ev.reqCerts}</Text>
                  </Group>
                )}

                {/* Check-in code */}
                {ev.checkInCode && (
                  <Group gap={4} mb={4}>
                    <Text size="xs" c="dimmed">Check-in:</Text>
                    <Code style={{ fontFamily: 'DM Mono, monospace', fontSize: 11 }}>{ev.checkInCode}</Code>
                  </Group>
                )}

                {/* Shifts from extras */}
                {Array.isArray(extras.shifts) && (extras.shifts as unknown[]).length > 0 && (
                  <Text size="xs" c="dimmed" mb={4}>
                    {(extras.shifts as unknown[]).length} shift{(extras.shifts as unknown[]).length !== 1 ? 's' : ''} defined
                  </Text>
                )}

                {/* Sign Up (for open events) */}
                {(ev.status === 'planned' || ev.status === 'active') && !atCapacity && (
                  <Button
                    size="xs"
                    variant="light"
                    color="teal"
                    fullWidth
                    mt={4}
                    onClick={(e) => { e.stopPropagation(); handleSignUp(ev); }}
                  >
                    Sign Up
                  </Button>
                )}
              </Paper>
            );
          })}
        </SimpleGrid>
      )}

      {/* ── Create / Edit Modal ─────────────────────────────── */}
      <Modal
        opened={formOpened}
        onClose={closeForm}
        title={editingEvent ? 'Edit Event' : 'Create Event'}
        size="lg"
      >
        <Stack gap="sm">
          <TextInput
            label="Event Name"
            required
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.currentTarget.value }))}
          />
          <Select
            label="Incident"
            data={incidentSelectData}
            value={form.incidentId}
            onChange={v => setForm(f => ({ ...f, incidentId: v ?? '' }))}
            clearable
          />
          <Group grow>
            <Select
              label="Type"
              data={EVENT_TYPES}
              value={form.type}
              onChange={v => setForm(f => ({ ...f, type: v ?? 'other' }))}
            />
            <Select
              label="Status"
              data={STATUS_OPTIONS}
              value={form.status}
              onChange={v => setForm(f => ({ ...f, status: v ?? 'planned' }))}
            />
          </Group>
          <TextInput
            label="Location"
            value={form.location}
            onChange={e => setForm(f => ({ ...f, location: e.currentTarget.value }))}
          />
          <div style={{ position: 'relative' }}>
            <TextInput
              label="Address"
              value={form.address}
              onChange={e => {
                const val = e.currentTarget.value;
                setForm(f => ({ ...f, address: val }));
                searchAddress(val);
              }}
              onFocus={() => { if (addrSuggestions.length > 0) setAddrOpen(true); }}
              onBlur={() => setTimeout(() => setAddrOpen(false), 200)}
            />
            {addrOpen && addrSuggestions.length > 0 && (
              <Paper
                withBorder shadow="sm" radius="sm"
                style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, maxHeight: 200, overflowY: 'auto' }}
              >
                {addrSuggestions.map((s, i) => (
                  <Text
                    key={i} size="xs" p="xs"
                    style={{ cursor: 'pointer', borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                    onMouseDown={() => {
                      setForm(f => ({ ...f, address: s.display }));
                      setAddrOpen(false);
                    }}
                  >
                    {s.display}
                  </Text>
                ))}
              </Paper>
            )}
          </div>
          <Group grow>
            <DateInput
              label="Start Date"
              value={form.startDate}
              onChange={v => setForm(f => ({ ...f, startDate: v }))}
              clearable
            />
            <DateInput
              label="End Date"
              value={form.endDate}
              onChange={v => setForm(f => ({ ...f, endDate: v }))}
              clearable
            />
          </Group>
          <NumberInput
            label="Target Volunteers"
            min={0}
            value={form.targetVol}
            onChange={v => setForm(f => ({ ...f, targetVol: Number(v) || 0 }))}
          />
          <MultiSelect
            label="Required Certifications"
            placeholder="Select certifications..."
            data={certOptions}
            value={form.reqCerts ? form.reqCerts.split(',').map(s => s.trim()).filter(Boolean) : []}
            onChange={vals => setForm(f => ({ ...f, reqCerts: vals.join(', ') }))}
            searchable
            clearable
          />
          <Group gap="xs" align="flex-end">
            <TextInput
              label="Check-in Code"
              value={form.checkInCode}
              onChange={e => setForm(f => ({ ...f, checkInCode: e.currentTarget.value }))}
              style={{ flex: 1 }}
              styles={{ input: { fontFamily: 'DM Mono, monospace' } }}
            />
            <Button
              variant="light"
              size="sm"
              onClick={() => setForm(f => ({ ...f, checkInCode: generateCheckInCode() }))}
            >
              Generate
            </Button>
          </Group>
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={closeForm}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>
              {editingEvent ? 'Save Changes' : 'Create Event'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Delete Confirmation ─────────────────────────────── */}
      <Modal opened={deleteOpened} onClose={closeDelete} title="Delete Event" size="sm">
        <Text size="sm" mb="md">
          Delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="subtle" size="xs" onClick={closeDelete}>Cancel</Button>
          <Button color="red" size="xs" onClick={handleDelete}>Delete</Button>
        </Group>
      </Modal>

      {/* ── Event Detail Modal ─────────────────────────────── */}
      <Modal opened={!!viewEvent} onClose={() => setViewEvent(null)}
        title={viewEvent?.name || 'Event Details'} size="lg">
        {viewEvent && <EventDetailContent
          ev={viewEvent}
          incidents={incidents}
          assignments={assignments}
          volunteers={volunteers}
          canEdit={canEdit}
          onEdit={(ev) => { setViewEvent(null); openEdit(ev); }}
          onClose={() => setViewEvent(null)}
        />}
      </Modal>
    </div>
  );
}

function EventDetailContent({ ev, incidents, assignments, volunteers, canEdit, onEdit, onClose }: {
  ev: VolEvent; incidents: Array<{ id: unknown; name: string }>; assignments: VolAssignment[]; volunteers: Volunteer[];
  canEdit: boolean; onEdit: (ev: VolEvent) => void; onClose: () => void;
}) {
          const evExtras = parseExtras(ev.notes) as Record<string, string>;
          const inc = incidents.find(i => String(i.id) === String(ev.incidentId));
          const evAssignments = assignments.filter(a =>
            String(a.eventId) === String(ev.id) && a.status !== 'cancelled'
          );

          return (
            <Stack gap="sm">
              {/* Status + Type */}
              <Group gap="xs">
                <Badge color={TYPE_COLORS[ev.type] ?? 'gray'} tt="capitalize">{ev.type}</Badge>
                <Badge color={STATUS_COLORS[ev.status] ?? 'gray'} variant="outline" tt="capitalize">{ev.status}</Badge>
                {inc && <Badge variant="light" size="sm">{inc.name}</Badge>}
              </Group>

              {/* Details grid */}
              <SimpleGrid cols={2}>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Location</Text>
                  <Text size="sm" fw={500}>{ev.location || '—'}</Text>
                  {ev.address && <Text size="xs" c="dimmed">{ev.address}</Text>}
                </div>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Dates</Text>
                  <Text size="sm" fw={500}>
                    {ev.startDate ? dayjs(ev.startDate).format('MMM D, YYYY') : '—'}
                    {ev.endDate && ev.endDate !== ev.startDate ? ` — ${dayjs(ev.endDate).format('MMM D, YYYY')}` : ''}
                  </Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Volunteers</Text>
                  <Text size="sm" fw={500}>
                    {evAssignments.length}{ev.targetVol > 0 ? ` / ${ev.targetVol}` : ''} assigned
                  </Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Check-in Code</Text>
                  {ev.checkInCode ? (
                    <Code style={{ fontFamily: 'DM Mono, monospace', fontSize: 14 }}>{ev.checkInCode}</Code>
                  ) : (
                    <Text size="sm" c="dimmed">None</Text>
                  )}
                </div>
              </SimpleGrid>

              {/* Required certs */}
              {ev.reqCerts && (
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Required Certifications</Text>
                  <Group gap={4} mt={2}>
                    {ev.reqCerts.split(',').map(c => c.trim()).filter(Boolean).map(c => (
                      <Badge key={c} size="xs" variant="outline" color="teal">{c}</Badge>
                    ))}
                  </Group>
                </div>
              )}

              {/* Description (from extras) */}
              {evExtras.description && (
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Description</Text>
                  <Text size="sm">{String(evExtras.description)}</Text>
                </div>
              )}

              {/* Assigned volunteers */}
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                  Assigned Volunteers ({evAssignments.length})
                </Text>
                {evAssignments.length === 0 ? (
                  <Text size="sm" c="dimmed">No volunteers assigned yet.</Text>
                ) : (
                  <Stack gap={4} style={{ maxHeight: 250, overflowY: 'auto' }}>
                    {evAssignments.map(a => {
                      const vol = volunteers.find(v => String(v.id) === a.volunteerId);
                      const aExtras = parseExtras(a.notes);
                      const name = vol
                        ? `${vol.firstName} ${vol.lastName}`
                        : (aExtras.volunteerName as string) || `Vol #${a.volunteerId}`;
                      return (
                        <Group key={a.id} justify="space-between" p="xs"
                          style={{ background: 'var(--mantine-color-gray-0)', borderRadius: 6 }}>
                          <div>
                            <Text size="sm" fw={500}>{name}</Text>
                            <Text size="xs" c="dimmed">
                              {a.role || '—'} · {a.shiftDate || '—'}
                              {a.startTime ? ` · ${a.startTime}–${a.endTime}` : ''}
                            </Text>
                          </div>
                          <Group gap={4}>
                            <Badge size="xs" color={
                              a.status === 'completed' ? 'green' :
                              a.status === 'confirmed' ? 'blue' :
                              a.status === 'no-show' ? 'red' : 'gray'
                            }>{a.status}</Badge>
                            {a.checkIn && <Badge size="xs" variant="light" color="teal">Checked In</Badge>}
                          </Group>
                        </Group>
                      );
                    })}
                  </Stack>
                )}
              </div>

              {/* Actions */}
              <Group justify="flex-end" mt="sm">
                {canEdit && (
                  <Button variant="light" size="xs" onClick={() => onEdit(ev)}>
                    Edit Event
                  </Button>
                )}
                <Button variant="default" size="xs" onClick={onClose}>Close</Button>
              </Group>
            </Stack>
          );
}
